import React, { useState, useEffect, useCallback } from 'react';
import usersApi from '../../api/usersApi';
import companiesApi from '../../api/companiesApi';
import styles from './SuperAdminsPage.module.css';

const SuperAdminsPage = () => {
  const [superAdmins, setSuperAdmins] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newUsername, setNewUsername] = useState('');
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [passwordResettingId, setPasswordResettingId] = useState(null);

  const [passwordModal, setPasswordModal] = useState(null); // { username, password }

  const [editingAdmin, setEditingAdmin] = useState(null);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState(new Set());
  const [updating, setUpdating] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [list, compList] = await Promise.all([
        usersApi.getSuperAdmins(),
        companiesApi.getCompanies(),
      ]);
      setSuperAdmins(list);
      setCompanies(compList);
    } catch (err) {
      console.error('Error cargando datos:', err);
      alert('Error cargando datos.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAdd = async () => {
    const uname = newUsername.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!uname || !emailRegex.test(uname)) return alert('Ingresa un correo válido.');
    setCreating(true);
    try {
      const { generatedPassword } = await usersApi.createSuperuser(uname);
      setNewUsername('');
      setPasswordModal({ username: uname, password: generatedPassword });
      await loadData();
    } catch (err) {
      const msg = err.response?.data?.message || 'Error creando super administrador.';
      alert(msg);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este super administrador?')) return;
    setDeletingId(id);
    try {
      await usersApi.deleteUser(id);
      await loadData();
    } catch (err) {
      alert('Error eliminando super administrador.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleResetPassword = async (admin) => {
    if (!window.confirm(
      `¿Generar una nueva contraseña genérica para ${admin.username}?\n` +
      'El usuario deberá cambiarla en su próximo inicio de sesión.'
    )) return;
    setPasswordResettingId(admin._id);
    try {
      const { generatedPassword } = await usersApi.resetUserPassword(admin._id);
      setPasswordModal({ username: admin.username, password: generatedPassword });
    } catch (err) {
      alert('Error reseteando contraseña.');
    } finally {
      setPasswordResettingId(null);
    }
  };

  const openCompanyModal = (admin) => {
    setEditingAdmin(admin);
    setSelectedCompanyIds(new Set(admin.companies || []));
  };

  const closeModal = () => {
    setEditingAdmin(null);
    setSelectedCompanyIds(new Set());
  };

  const toggleCompany = (companyId) => {
    setSelectedCompanyIds(prev => {
      const next = new Set(prev);
      if (next.has(companyId)) next.delete(companyId);
      else next.add(companyId);
      return next;
    });
  };

  const selectAll = () => setSelectedCompanyIds(new Set(companies.map(c => c._id)));
  const deselectAll = () => setSelectedCompanyIds(new Set());

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      await usersApi.updateUser(editingAdmin._id, { companies: [...selectedCompanyIds] });
      await loadData();
      closeModal();
    } catch (err) {
      alert('Error actualizando acceso.');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.header}>Super Administradores</h2>

      <div className={styles.form}>
        <input
          type="text"
          className={styles.input}
          placeholder="Correo electrónico"
          value={newUsername}
          onChange={e => setNewUsername(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleAdd()}
        />
        <button className={styles.button} onClick={handleAdd} disabled={creating}>
          {creating ? 'Creando…' : 'Agregar'}
        </button>
      </div>

      {loading ? (
        <p>Cargando...</p>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Usuario</th>
              <th>Empresas con acceso</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {superAdmins.length > 0 ? superAdmins.map(admin => (
              <tr key={admin._id}>
                <td>{admin.username}</td>
                <td>{(admin.companies || []).length}</td>
                <td className={styles.actions}>
                  <button
                    className={styles.button}
                    onClick={() => openCompanyModal(admin)}
                  >
                    Empresas
                  </button>
                  <button
                    className={`${styles.button} ${styles.secondaryButton}`}
                    onClick={() => handleResetPassword(admin)}
                    disabled={passwordResettingId === admin._id}
                  >
                    {passwordResettingId === admin._id ? 'Generando…' : 'Contraseña'}
                  </button>
                  <button
                    className={`${styles.button} ${styles.deleteButton}`}
                    onClick={() => handleDelete(admin._id)}
                    disabled={deletingId === admin._id}
                  >
                    {deletingId === admin._id ? 'Eliminando…' : 'Eliminar'}
                  </button>
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan={3} style={{ textAlign: 'center' }}>
                  No hay super administradores.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {/* Modal: contraseña generada */}
      {passwordModal && (
        <div className={styles.modalOverlay} onClick={() => setPasswordModal(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>Contraseña generada</h3>
            <p className={styles.modalText}>
              Anota y comparte esta contraseña con <strong>{passwordModal.username}</strong>.
              En su próximo inicio de sesión se le pedirá cambiarla.
            </p>
            <div className={styles.passwordBox}>
              <strong>{passwordModal.password}</strong>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.button} onClick={() => setPasswordModal(null)}>
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}

      {editingAdmin && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>
              Acceso de empresas — {editingAdmin.username}
            </h3>
            <div className={styles.modalActions}>
              <button
                className={`${styles.button} ${styles.smallButton}`}
                onClick={selectAll}
              >
                Seleccionar todas
              </button>
              <button
                className={`${styles.button} ${styles.smallButton} ${styles.secondaryButton}`}
                onClick={deselectAll}
              >
                Deseleccionar todas
              </button>
            </div>
            <div className={styles.companyList}>
              {companies.length > 0 ? companies.map(company => (
                <label key={company._id} className={styles.companyItem}>
                  <input
                    type="checkbox"
                    checked={selectedCompanyIds.has(company._id)}
                    onChange={() => toggleCompany(company._id)}
                  />
                  <span>{company.name}</span>
                </label>
              )) : (
                <p>No hay empresas registradas.</p>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.button} onClick={handleUpdate} disabled={updating}>
                {updating ? 'Actualizando…' : 'Actualizar'}
              </button>
              <button
                className={`${styles.button} ${styles.secondaryButton}`}
                onClick={closeModal}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminsPage;
